import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  DescriptionOutlined as PayslipIcon,
  AttachMoneyOutlined as MoneyIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import MobileMonthYearFilters from "../../components/mobile/MobileMonthYearFilters";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const PayslipCard = ({ record, onDownload, monthLabel, employeeMode }) => {
  const netSalary =
    record.salary?.net_salary != null
      ? Number(record.salary.net_salary).toFixed(2)
      : "N/A";
  const generatedAt = record.generated_at
    ? new Date(record.generated_at).toLocaleDateString("en-IN")
    : "—";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 36px",
            alignItems: "center",
            columnGap: 0.75,
            mb: 0.35,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {employeeMode ? monthLabel : record.employee_name || "Unknown"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onDownload(record)}
            aria-label="Download payslip"
            sx={{
              p: 0,
              color: ORANGE,
              bgcolor: BRAND.orangeLight,
              width: 36,
              height: 36,
            }}
          >
            <DownloadIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Box>
        {!employeeMode && (
          <Typography
            sx={{ fontSize: "0.6875rem", fontWeight: 600, color: ORANGE, mb: 0.35 }}
          >
            {record.employee_id || record.emp_id || "N/A"} · {monthLabel}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
            Net: <strong style={{ color: NAVY }}>₹{netSalary}</strong>
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.border }}>
            ·
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
            Generated {generatedAt}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const PayslipsMobileView = ({
  payslips,
  searchTerm,
  setSearchTerm,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  monthOptions,
  yearOptions,
  onDownload,
  periodLabel,
  employeeMode = false,
}) => {
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return payslips;
    const q = searchTerm.toLowerCase();
    if (employeeMode) {
      return payslips.filter((p) => periodLabel.toLowerCase().includes(q));
    }
    return payslips.filter(
      (p) =>
        p.employee_name?.toLowerCase().includes(q) ||
        String(p.employee_id || p.emp_id || "")
          .toLowerCase()
          .includes(q)
    );
  }, [payslips, searchTerm, employeeMode, periodLabel]);

  const stats = useMemo(
    () => ({
      count: filteredList.length,
      totalNet: filteredList.reduce(
        (sum, p) => sum + Number(p.salary?.net_salary || 0),
        0
      ),
    }),
    [filteredList]
  );

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        {employeeMode ? "My Payslips" : "Payslips"}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        {periodLabel}
      </Typography>

      <MobileMonthYearFilters
        sx={{ mb: 1.5 }}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        monthOptions={monthOptions}
        yearOptions={yearOptions}
      />

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<PayslipIcon sx={{ fontSize: "1rem" }} />}
            label={employeeMode ? "This Period" : "Payslips"}
            value={stats.count}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
        </Grid>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<MoneyIcon sx={{ fontSize: "1rem" }} />}
            label="Net Total"
            value={`₹${stats.totalNet.toFixed(0)}`}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
          />
        </Grid>
      </Grid>

      {!employeeMode && (
        <TextField
          fullWidth
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: "12px",
              "& fieldset": { borderColor: BRAND.border },
              "&.Mui-focused fieldset": { borderColor: ORANGE },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: BRAND.muted }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {filteredList.length > 0 ? (
        filteredList.map((record) => (
          <PayslipCard
            key={record.id}
            record={record}
            onDownload={onDownload}
            monthLabel={periodLabel}
            employeeMode={employeeMode}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <PayslipIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No payslips found
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            No records for {periodLabel}. Try another month or year.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PayslipsMobileView;
